import React, { useState, useEffect } from 'react';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, addDoc, getDocs, query, where, Timestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, secondaryAuth } from '../components/firebase-config.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserPlus, faInfoCircle, faClock, faCalendarPlus, faCheck, faTimes, faSpinner,
  faClinicMedical, faUser, faEnvelope, faPhone, faCalendarAlt, faQrcode, faLink,
  faDownload, faEdit, faTrash, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Button, Form, Alert, Tabs, Tab, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';

const MedecinPatientManager = ({ medecinId, affiliatedStructures, onPatientAdded, onRdvAdded, onPatientUpdated, onPatientDeleted }) => {
  // États pour le formulaire patient
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientForm, setPatientForm] = useState({
    nom: '',
    dateNaissance: '',
    sexe: 'non-specifie',
    email: '',
    telephone: '',
    adresse: '',
    assurances: [],
    diagnostics: '',
    antecedentsMedicaux: ''
  });
  
  // États pour le formulaire de rendez-vous
  const [showRdvModal, setShowRdvModal] = useState(false);
  const [rdvForm, setRdvForm] = useState({
    patientId: '',
    date: new Date().toISOString().split('T')[0],
    heure: '08:00',
    duree: 30,
    motif: '',
    statut: 'confirmé',
    typeConsultation: 'presentiel',
    structureId: ''
  });
  
  // États pour les données
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [assurancesList] = useState([
    "CNAM", "CNSS", "CNRPS", "Assurance privée", "Autre", "Sanlam", "AXA", 
    "NSIA", "Saham Assurance", "Leadway Assurance", "Mutuelle des Fonctionnaires", 
    "SONAM", "Allianz", "Liberty Mutual", "Activa Assurance", "SUNU Assurances"
  ]);
  
  // États pour le code QR
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrStructureId, setQrStructureId] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [qrGenerated, setQrGenerated] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [medecinInfo, setMedecinInfo] = useState(null);

  // Nouveaux états pour la modification et suppression
  const [editMode, setEditMode] = useState(false);
  const [currentPatientId, setCurrentPatientId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const MAYTAPI_CONFIG = {
    PRODUCT_ID: "5e994f76-4f49-4057-aca4-6bef3b1de795",
    API_TOKEN: "a0b5ba45-b4da-429e-ba5b-127851d9b92e",
    PHONE_ID: "77695",
  }

  // Fonction pour générer un mot de passe aléatoire
  const generateRandomPassword = (length = 8) => {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };
  
  // Fonction pour générer un code de vérification
  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };
  
  // Charger les patients du médecin
  useEffect(() => {
    const fetchPatients = async () => {
      if (!medecinId) return;
      
      try {
        setLoading(true);
        const patientsQuery = query(
          collection(db, "patients"),
          where("medecinId", "==", medecinId)
        );
        const querySnapshot = await getDocs(patientsQuery);
        const patientsList = [];
        querySnapshot.forEach((doc) => {
          patientsList.push({ id: doc.id, ...doc.data() });
        });
        setPatients(patientsList);
      } catch (error) {
        console.error("Erreur lors du chargement des patients:", error);
        setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
      } finally {
        setLoading(false);
      }
    };
    
    fetchPatients();
  }, [medecinId]);
  
  // Charger les informations du médecin
  useEffect(() => {
    const fetchMedecinInfo = async () => {
      if (!medecinId) return;
      
      try {
        const medecinDoc = await getDoc(doc(db, "medecins", medecinId));
        if (medecinDoc.exists()) {
          setMedecinInfo(medecinDoc.data());
        }
      } catch (error) {
        console.error("Erreur lors du chargement des informations du médecin:", error);
      }
    };
    
    fetchMedecinInfo();
  }, [medecinId]);
  
  // Gestion des formulaires
  const handlePatientChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "assurances") {
      // Pour les checkboxes d'assurances
      const updatedArray = [...patientForm.assurances];
      if (checked) {
        updatedArray.push(value);
      } else {
        const index = updatedArray.indexOf(value);
        if (index > -1) {
          updatedArray.splice(index, 1);
        }
      }
      setPatientForm({ ...patientForm, [name]: updatedArray });
    } else {
      setPatientForm({ ...patientForm, [name]: value });
    }
  };
  
  const handleRdvChange = (e) => {
    const { name, value } = e.target;
    setRdvForm({ ...rdvForm, [name]: value });
  };
  
  // Fonction pour envoyer un message WhatsApp
  const sendWhatsAppMessage = async (phoneNumber, message) => {
    try {
      console.log("⏳ Préparation de l'envoi WhatsApp...");
      console.log("📱 Numéro de téléphone:", phoneNumber);

      // Formater le numéro de téléphone pour WhatsApp
      if (!phoneNumber) {
        console.error("❌ Numéro de téléphone manquant!");
        return { success: false, error: "Numéro de téléphone manquant" };
      }

      let formattedNumber = phoneNumber;
      // Retirer tous les espaces, tirets et parenthèses
      formattedNumber = formattedNumber.replace(/[\s\-()]/g, "");

      // S'assurer que le numéro commence par +
      if (!formattedNumber.startsWith("+")) {
        // Si le numéro commence par un 0, le remplacer par le code pays
        if (formattedNumber.startsWith("0")) {
          formattedNumber = "+221" + formattedNumber.substring(1);
        } else {
          // Sinon, simplement ajouter le code pays
          formattedNumber = "+221" + formattedNumber;
        }
      }

      console.log("📞 Numéro formaté:", formattedNumber);
      console.log("💬 Message à envoyer:", message);

      // Vérifier la configuration Maytapi
      if (
        !MAYTAPI_CONFIG.PRODUCT_ID ||
        !MAYTAPI_CONFIG.PHONE_ID ||
        !MAYTAPI_CONFIG.API_TOKEN
      ) {
        console.error("❌ Configuration Maytapi incomplète:", MAYTAPI_CONFIG);
        return { success: false, error: "Configuration Maytapi incomplète" };
      }

      // Configurer la requête pour l'API Maytapi
      console.log("🔄 Envoi de la requête à Maytapi...");
      console.log("📊 Configuration API:", {
        productId: MAYTAPI_CONFIG.PRODUCT_ID,
        phoneId: MAYTAPI_CONFIG.PHONE_ID,
      });

      const response = await axios.post(
        `https://api.maytapi.com/api/${MAYTAPI_CONFIG.PRODUCT_ID}/${MAYTAPI_CONFIG.PHONE_ID}/sendMessage`,
        {
          to_number: formattedNumber,
          type: "text",
          message: message,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-maytapi-key": MAYTAPI_CONFIG.API_TOKEN,
          },
        }
      );

      console.log("✅ Réponse reçue de Maytapi:", response.data);

      if (response.status === 200 && response.data.success) {
        console.log("🎉 Message WhatsApp envoyé avec succès!");
        return { success: true, data: response.data };
      } else {
        console.error(
          "❌ Échec de l'envoi du message. Réponse API:",
          response.data
        );
        throw new Error(response.data.message || "Échec de l'envoi du message");
      }
    } catch (error) {
      console.error("❌ Erreur lors de l'envoi du message WhatsApp:", error);
      return { success: false, error: error.message };
    }
  };
  
  // Ajouter un patient
  const handleAddPatient = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (editMode) {
        // Mise à jour d'un patient existant
        await updateDoc(doc(db, "patients", currentPatientId), {
          ...patientForm,
          updatedAt: Timestamp.now()
        });
        
        // Mettre à jour la liste des patients
        const updatedPatients = patients.map(p => 
          p.id === currentPatientId ? { id: currentPatientId, ...patientForm, medecinId, updatedAt: Timestamp.now() } : p
        );
        
        setPatients(updatedPatients);
        
        // Callback
        if (onPatientUpdated) {
          onPatientUpdated({ id: currentPatientId, ...patientForm, medecinId });
        }
        
        setMessage({ type: 'success', text: 'Patient mis à jour avec succès!' });
      } else {
        // Ajout d'un nouveau patient
        // Générer un mot de passe aléatoire
        const generatedPassword = generateRandomPassword(8);
        
        // Créer un compte utilisateur pour le patient
        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          patientForm.email,
          generatedPassword
        );
        
        const patientId = userCredential.user.uid;
        
        // Enregistrer les données du patient dans Firestore
        await setDoc(doc(db, "patients", patientId), {
          ...patientForm,
          medecinId: medecinId,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        
        // Envoyer un message WhatsApp avec les identifiants
        const welcomeMessage = 
          `Bonjour  ${patientForm.nom},\n\n` +
          `Votre compte patient a été créé avec succès!\n\n` +
          `Voici vos informations de connexion:\n` +
          `Email: ${patientForm.email}\n` +
          `Mot de passe: ${generatedPassword}\n\n` +
          `Vous pouvez désormais accéder à votre compte en vous connectant à l'adresse suivante: \n\n https://www.samdigitale.com/Auth\n\n` +
          `Nous vous recommandons de changer votre mot de passe après votre première connexion.`;
        
        await sendWhatsAppMessage(patientForm.telephone, welcomeMessage);
        
        // Mettre à jour la liste des patients
        const newPatient = {
          id: patientId,
          ...patientForm,
          medecinId: medecinId,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };
        
        setPatients([...patients, newPatient]);
        
        // Callback
        if (onPatientAdded) {
          onPatientAdded(newPatient);
        }
        
        setMessage({ type: 'success', text: 'Patient ajouté avec succès! Identifiants envoyés par WhatsApp.' });
      }
      
      setShowPatientModal(false);
      
      // Réinitialiser le formulaire et l'état d'édition
      setPatientForm({
        nom: '',
        dateNaissance: '',
        sexe: 'non-specifie',
        email: '',
        telephone: '',
        adresse: '',
        assurances: [],
        diagnostics: '',
        antecedentsMedicaux: ''
      });
      setEditMode(false);
      setCurrentPatientId(null);
      
    } catch (error) {
      console.error("Erreur lors de l'ajout/modification du patient:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Modifier un patient
  const handleEditPatient = (patient) => {
    setPatientForm({
      nom: patient.nom || '',
      dateNaissance: patient.dateNaissance || '',
      sexe: patient.sexe || 'non-specifie',
      email: patient.email || '',
      telephone: patient.telephone || '',
      adresse: patient.adresse || '',
      assurances: patient.assurances || [],
      diagnostics: patient.diagnostics || '',
      antecedentsMedicaux: patient.antecedentsMedicaux || ''
    });
    
    setEditMode(true);
    setCurrentPatientId(patient.id);
    setShowPatientModal(true);
  };
  
  // Supprimer un patient
  const handleDeletePatient = async () => {
    if (!patientToDelete) return;
    
    try {
      setLoading(true);
      
      // Vérifier s'il y a des rendez-vous liés à ce patient
      const rdvsQuery = query(
        collection(db, "rendezvous"),
        where("patientId", "==", patientToDelete.id)
      );
      
      const rdvsSnapshot = await getDocs(rdvsQuery);
      
      // Supprimer tous les rendez-vous liés
      const deletePromises = [];
      rdvsSnapshot.forEach((rdvDoc) => {
        deletePromises.push(deleteDoc(doc(db, "rendezvous", rdvDoc.id)));
      });
      
      await Promise.all(deletePromises);
      
      // Supprimer le patient
      await deleteDoc(doc(db, "patients", patientToDelete.id));
      
      // Mettre à jour la liste des patients
      const updatedPatients = patients.filter(p => p.id !== patientToDelete.id);
      setPatients(updatedPatients);
      
      // Callback
      if (onPatientDeleted) {
        onPatientDeleted(patientToDelete);
      }
      
      setMessage({ 
        type: 'success', 
        text: `Patient  ${patientToDelete.nom} supprimé avec succès.${
          rdvsSnapshot.size > 0 ? ` ${rdvsSnapshot.size} rendez-vous associés ont également été supprimés.` : ''
        }`
      });
      
    } catch (error) {
      console.error("Erreur lors de la suppression du patient:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setPatientToDelete(null);
    }
  };
  
  // Ajouter un rendez-vous
  const handleAddRdv = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Vérifier que tous les champs requis sont remplis
      if (!rdvForm.patientId || !rdvForm.date || !rdvForm.heure || !rdvForm.structureId) {
        setMessage({ type: 'danger', text: 'Veuillez remplir tous les champs obligatoires' });
        return;
      }
      
      // Récupérer les informations du patient
      const patientData = patients.find(p => p.id === rdvForm.patientId);
      
      // Récupérer les informations du médecin
      const medecinDoc = await getDoc(doc(db, "medecins", medecinId));
      const medecinData = medecinDoc.exists() ? medecinDoc.data() : null;
      
      // Récupérer les informations de la structure
      const structureDoc = await getDoc(doc(db, "structures", rdvForm.structureId));
      const structureData = structureDoc.exists() ? structureDoc.data() : null;
      
      // Générer un code de vérification pour le rendez-vous
      const verificationCode = generateVerificationCode();
      
      // Créer l'objet rendez-vous
      const rdvData = {
        ...rdvForm,
        medecinId: medecinId,
        medecinNom: medecinData ? `${medecinData.titre || "Dr"} ${medecinData.nom}` : 'Dr.',
        medecinSpecialite: medecinData?.specialite || '',
        patientNom: patientData ? ` ${patientData.nom}` : '',
        patientTelephone: patientData?.telephone || '',
        patientEmail: patientData?.email || '',
        structureNom: structureData?.nom || '',
        verificationCode: verificationCode,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      // Ajouter le rendez-vous à Firestore
      const docRef = await addDoc(collection(db, "rendezvous"), rdvData);
      
      // Envoyer une notification au patient
      const formattedDate = new Date(rdvForm.date).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      
      const notificationMessage = 
        `Bonjour ${patientData?.nom},\n\n` +
        `Votre ${rdvForm.typeConsultation === "teleconsultation" ? "téléconsultation" : "rendez-vous"} ` +
        `a été confirmé pour le ${formattedDate} à ${rdvForm.heure} avec ${medecinData ? `${medecinData.titre || "Dr"} ${medecinData.nom}` : 'votre médecin'}.\n\n` +
        `Motif: ${rdvForm.motif || "Consultation"}` +
        `${rdvForm.typeConsultation === "teleconsultation" ? "\n\n" : ""} `+
        `Code de vérification: ${verificationCode}\n\n` +
        `Vous pouvez vérifier l'état de votre rendez-vous en utilisant ce code.\n\n` +
        `Vous pouvez désormais accéder à votre compte en vous connectant à l'adresse suivante: \n\n https://www.samdigitale.com/Auth\n\n` +
        `\n\nMerci de votre confiance.`; 
      
      if (patientData?.telephone) {
        await sendWhatsAppMessage(patientData.telephone, notificationMessage);
      }
      
      // Callback
      if (onRdvAdded) {
        onRdvAdded({
          id: docRef.id,
          ...rdvData
        });
      }
      
      setMessage({ type: 'success', text: 'Rendez-vous ajouté avec succès! Notification envoyée au patient.' });
      setShowRdvModal(false);
      
      // Réinitialiser le formulaire
      setRdvForm({
        patientId: '',
        date: new Date().toISOString().split('T')[0],
        heure: '08:00',
        duree: 30,
        motif: '',
        statut: 'confirmé',
        typeConsultation: 'presentiel',
        structureId: ''
      });
      
    } catch (error) {
      console.error("Erreur lors de l'ajout du rendez-vous:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Générer un QR code pour la page de rendez-vous du médecin
  const handleGenerateQR = async () => {
    try {
      setLoading(true);
      
      if (!qrStructureId) {
        setMessage({ type: 'danger', text: 'Veuillez sélectionner une structure pour le QR code' });
        return;
      }
      
      // Récupérer les informations de la structure
      const structureDoc = await getDoc(doc(db, "structures", qrStructureId));
      const structureData = structureDoc.exists() ? structureDoc.data() : null;
      
      if (!structureData) {
        setMessage({ type: 'danger', text: 'Structure introuvable' });
        return;
      }
      
      // Construire l'URL pour le QR code
      // Cette URL mènera à une page unique où le patient pourra choisir entre prendre un RDV ou vérifier un RDV
      const baseUrl = 'http://localhost:3000/patient-portal';
      const params = new URLSearchParams();
      
      params.append('medecinId', medecinId);
      params.append('structureId', qrStructureId);
      
      if (medecinInfo) {
        params.append('medecinNom', ` ${medecinInfo.nom}`);
        params.append('medecinSpecialite', medecinInfo.specialite || '');
      }
      
      params.append('structureNom', structureData.nom || '');
      
      const fullUrl = `${baseUrl}?${params.toString()}`;
      setQrUrl(fullUrl);
      setQrGenerated(true);
      
    } catch (error) {
      console.error("Erreur lors de la génération du QR code:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Télécharger le QR code
  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code');
    if (canvas) {
      const pngUrl = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qrcode-dr-${medecinInfo ? medecinInfo.nom.toLowerCase() : 'medecin'}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };
  
  // Copier l'URL du QR code dans le presse-papier
  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrUrl).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      },
      (err) => {
        console.error('Erreur lors de la copie:', err);
      }
    );
  };

  // Filtrer les patients en fonction du terme de recherche
  const filteredPatients = patients.filter(patient => {
    const searchLower = searchTerm.toLowerCase();
    return (
      patient.nom.toLowerCase().includes(searchLower) ||
      patient.email.toLowerCase().includes(searchLower) ||
      patient.telephone.includes(searchTerm)
    );
  });

  return (
    <div className="patient-rdv-manager">
      {/* Message de notification */}
      {message && (
        <Alert 
          variant={message.type} 
          onClose={() => setMessage(null)} 
          dismissible
          className="mb-3"
        >
          {message.text}
        </Alert>
      )}
      
      <div className="d-flex justify-content-between mb-4 flex-wrap">
        <Button 
          variant="primary" 
          onClick={() => {
            setEditMode(false);
            setCurrentPatientId(null);
            setPatientForm({
              nom: '',
              dateNaissance: '',
              sexe: 'non-specifie',
              email: '',
              telephone: '',
              adresse: '',
              assurances: [],
              diagnostics: '',
              antecedentsMedicaux: ''
            });
            setShowPatientModal(true);
          }}
          disabled={loading}
          className="mb-2 mb-md-0 d-none d-md-block"
        >
          <FontAwesomeIcon icon={faUserPlus} className="me-2" />
          Ajouter un patient
        </Button>
        <Button 
          variant="primary" 
          onClick={() => {
            setEditMode(false);
            setCurrentPatientId(null);
            setPatientForm({
              nom: '',
              dateNaissance: '',
              sexe: 'non-specifie',
              email: '',
              telephone: '',
              adresse: '',
              assurances: [],
              diagnostics: '',
              antecedentsMedicaux: ''
            });
            setShowPatientModal(true);
          }}
          disabled={loading}
          className="mb-2 mb-md-0 d-block d-md-none"
        >
          <FontAwesomeIcon icon={faUserPlus} className="me-0" />
           Patient
        </Button>
        
        <Button 
          variant="success" 
          onClick={() => setShowRdvModal(true)}
          disabled={loading || patients.length === 0}
          className="mb-2 mb-md-0 d-none d-md-block"
        >
          <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
          Ajouter un rendez-vous
        </Button>
        <Button 
          variant="success" 
          onClick={() => setShowRdvModal(true)}
          disabled={loading || patients.length === 0}
          className="mb-2 mb-md-0 d-block d-md-none"
        >
          <FontAwesomeIcon icon={faCalendarPlus} className="me-0" />
          Rendez-vous
        </Button>
        
        <Button 
          variant="info" 
          onClick={() => {
            setShowQRModal(true);
            setQrGenerated(false);
            setQrStructureId('');
          }}
          disabled={loading}
          className="text-white mb-2 mb-md-0 d-none d-md-block"
        >
          <FontAwesomeIcon icon={faQrcode} className="me-2" />
          Générer QR code
        </Button>
        <Button 
          variant="info" 
          onClick={() => {
            setShowQRModal(true);
            setQrGenerated(false);
            setQrStructureId('');
          }}
          disabled={loading}
          className="text-white mb-2 mb-md-0 d-block d-md-none"
        >
          <FontAwesomeIcon icon={faQrcode} className="me-0" />
          QR code
        </Button>
      </div>
      
      {/* Modal pour ajouter/modifier un patient */}
      <Modal 
        show={showPatientModal} 
        onHide={() => {
          setShowPatientModal(false);
          setEditMode(false);
          setCurrentPatientId(null);
        }}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={editMode ? faEdit : faUserPlus} className="me-2" />
            {editMode ? "Modifier le patient" : "Ajouter un nouveau patient"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddPatient}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faUser} className="me-1" />
                    Prénom et Nom du patient <span className="text-muted">(Optionnel)</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nom"
                    value={patientForm.nom}
                    onChange={handlePatientChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faUser} className="me-1" />
                    Date de naissance <span className="text-muted">(Optionnel)</span>
                  </Form.Label>
                  <Form.Control
                                       type="date"
                                       name="dateNaissance"
                                       value={patientForm.dateNaissance}
                                       onChange={handlePatientChange}
                                     />
                                   </Form.Group>
                                 </div>
                                 <div className="col-md-6 mb-3">
                                   <Form.Group>
                                     <Form.Label>Sexe <span className="text-muted">(Optionnel)</span></Form.Label>
                                     <Form.Select
                                       name="sexe"
                                       value={patientForm.sexe}
                                       onChange={handlePatientChange}
                                     >
                                       <option value="non-specifie">Non spécifié</option>
                                       <option value="homme">Homme</option>
                                       <option value="femme">Femme</option>
                                     </Form.Select>
                                   </Form.Group>
                                 </div>
                               </div>
                               
                               <div className="row">
                                 <div className="col-md-6 mb-3">
                                   <Form.Group>
                                                          <Form.Label>
                       <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                       Email <span className="text-muted">(Optionnel)</span>
                     </Form.Label>
                     <Form.Control
                       type="email"
                       name="email"
                       value={patientForm.email}
                       onChange={handlePatientChange}
                       readOnly={editMode} // L'email ne peut pas être modifié en mode édition
                     />
                                     {editMode ? (
                                       <Form.Text className="text-muted">
                                         L'adresse email ne peut pas être modifiée
                                       </Form.Text>
                                     ) : (
                                       <Form.Text className="text-muted">
                                         Un compte sera créé avec cet email
                                       </Form.Text>
                                     )}
                                   </Form.Group>
                                 </div>
                                 <div className="col-md-6 mb-3">
                                   <Form.Group>
                                                          <Form.Label>
                       <FontAwesomeIcon icon={faPhone} className="me-1" />
                       Téléphone <span className="text-danger">(Obligatoire)</span>
                     </Form.Label>
                                     <Form.Control
                                       type="tel"
                                       name="telephone"
                                       value={patientForm.telephone}
                                       onChange={handlePatientChange}
                                       placeholder="Ex: 77 123 45 67"
                                       required
                                     />
                                     {!editMode && (
                                       <Form.Text className="text-muted">
                                         Les identifiants seront envoyés par WhatsApp
                                       </Form.Text>
                                     )}
                                   </Form.Group>
                                 </div>
                               </div>
                               
                               <Form.Group className="mb-3">
                                 <Form.Label>Adresse <span className="text-muted">(Optionnel)</span></Form.Label>
                                 <Form.Control
                                   type="text"
                                   name="adresse"
                                   value={patientForm.adresse}
                                   onChange={handlePatientChange}
                                 />
                               </Form.Group>
                               
                               <Form.Group className="mb-3">
                                 <Form.Label>Assurances <span className="text-muted">(Optionnel)</span></Form.Label>
                                 <div className="d-flex flex-wrap">
                                   {assurancesList.map((assurance) => (
                                     <div key={assurance} className="form-check me-3 mb-2">
                                       <input
                                         className="form-check-input"
                                         type="checkbox"
                                         id={`assurance-${assurance}`}
                                         name="assurances"
                                         value={assurance}
                                         checked={patientForm.assurances.includes(assurance)}
                                         onChange={handlePatientChange}
                                       />
                                       <label className="form-check-label" htmlFor={`assurance-${assurance}`}>
                                         {assurance}
                                       </label>
                                     </div>
                                   ))}
                                 </div>
                               </Form.Group>
                               
                               <Form.Group className="mb-3">
                                 <Form.Label>Diagnostics <span className="text-muted">(Optionnel)</span></Form.Label>
                                 <Form.Control
                                   as="textarea"
                                   rows={2}
                                   name="diagnostics"
                                   value={patientForm.diagnostics}
                                   onChange={handlePatientChange}
                                   placeholder="Diagnostics préliminaires"
                                 />
                               </Form.Group>
                               
                               <Form.Group className="mb-3">
                                 <Form.Label>Antécédents médicaux <span className="text-muted">(Optionnel)</span></Form.Label>
                                 <Form.Control
                                   as="textarea"
                                   rows={2}
                                   name="antecedentsMedicaux"
                                   value={patientForm.antecedentsMedicaux}
                                   onChange={handlePatientChange}
                                   placeholder="Antécédents médicaux importants"
                                 />
                               </Form.Group>
                               
                               <div className="d-flex justify-content-end mt-3">
                                 <Button
                                   variant="secondary"
                                   onClick={() => {
                                     setShowPatientModal(false);
                                     setEditMode(false);
                                     setCurrentPatientId(null);
                                   }}
                                   className="me-2"
                                   disabled={loading}
                                 >
                                   <FontAwesomeIcon icon={faTimes} className="me-1" />
                                   Annuler
                                 </Button>
                                 <Button
                                   variant={editMode ? "success" : "primary"}
                                   type="submit"
                                   disabled={loading}
                                 >
                                   {loading ? (
                                     <>
                                       <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                       {editMode ? "Mise à jour en cours..." : "Ajout en cours..."}
                                     </>
                                   ) : (
                                     <>
                                       <FontAwesomeIcon icon={editMode ? faEdit : faUserPlus} className="me-1" />
                                       {editMode ? "Mettre à jour le patient" : "Ajouter le patient"}
                                     </>
                                   )}
                                 </Button>
                               </div>
                             </Form>
                           </Modal.Body>
                         </Modal>
                         
                         {/* Modal pour ajouter un rendez-vous */}
                         <Modal 
                           show={showRdvModal} 
                           onHide={() => setShowRdvModal(false)}
                           backdrop="static"
                         >
                           <Modal.Header closeButton>
                             <Modal.Title>
                               <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
                               Ajouter un rendez-vous
                             </Modal.Title>
                           </Modal.Header>
                           <Modal.Body>
                             <Form onSubmit={handleAddRdv}>
                               <Form.Group className="mb-3">
                                 <Form.Label>Patient <span className="text-danger">(Obligatoire)</span></Form.Label>
                                 <Form.Select
                                   name="patientId"
                                   value={rdvForm.patientId}
                                   onChange={handleRdvChange}
                                   required
                                 >
                                   <option value="">Sélectionner un patient</option>
                                   {patients.map(patient => (
                                     <option key={patient.id} value={patient.id}>
                                       {patient.nom} - {patient.telephone}
                                     </option>
                                   ))}
                                 </Form.Select>
                               </Form.Group>
                               
                               <Form.Group className="mb-3">
                                 <Form.Label>Structure ou Service spécialisé <span className="text-danger">(Obligatoire)</span></Form.Label>
                                 <Form.Select
                                   name="structureId"
                                   value={rdvForm.structureId}
                                   onChange={handleRdvChange}
                                   required
                                 >
                                   <option value="">Sélectionner une structure</option>
                                   {affiliatedStructures.map(structure => (
                                     <option key={structure.id} value={structure.id}>
                                       {structure.nom}
                                     </option>
                                   ))}
                                 </Form.Select>
                               </Form.Group>
                               
                               <div className="row">
                                 <div className="col-md-6 mb-3">
                                   <Form.Group>
                                     <Form.Label>
                                       <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                       Date <span className="text-danger">(Obligatoire)</span>
                                     </Form.Label>
                                     <Form.Control
                                       type="date"
                                       name="date"
                                       value={rdvForm.date}
                                       onChange={handleRdvChange}
                                       min={new Date().toISOString().split('T')[0]}
                                       required
                                     />
                                   </Form.Group>
                                 </div>
                                 <div className="col-md-6 mb-3">
                                   <Form.Group>
                                     <Form.Label>
                                       <FontAwesomeIcon icon={faClock} className="me-1" />
                                       Heure <span className="text-danger">(Obligatoire)</span>
                                     </Form.Label>
                                     <Form.Control
                                       type="time"
                                       name="heure"
                                       value={rdvForm.heure}
                                       onChange={handleRdvChange}
                                       required
                                     />
                                   </Form.Group>
                                 </div>
                               </div>
                              
                               
                               {/*
                                
                               <div className="row">
                                 <div className="col-md-6 mb-3">
                                   <Form.Group>
                                     <Form.Label>Type de consultation</Form.Label>
                                     <Form.Select
                                       name="typeConsultation"
                                       value={rdvForm.typeConsultation}
                                       onChange={handleRdvChange}
                                     >
                                       <option value="presentiel">En présentiel</option>
                                       <option value="teleconsultation">Téléconsultation</option>
                                     </Form.Select>
                                   </Form.Group>
                                 </div>
                                 <div className="col-md-6 mb-3">
                                   <Form.Group>
                                     <Form.Label>Durée (minutes)</Form.Label>
                                     <Form.Select
                                       name="duree"
                                       value={rdvForm.duree}
                                       onChange={handleRdvChange}
                                     >
                                       <option value="15">15 min</option>
                                       <option value="30">30 min</option>
                                       <option value="45">45 min</option>
                                       <option value="60">60 min</option>
                                     </Form.Select>
                                   </Form.Group>
                                 </div>
                               </div>
                               
                               */}
                               <Form.Group className="mb-3">
                                 <Form.Label>Motif de consultation</Form.Label>
                                 <Form.Control
                                   as="textarea"
                                   rows={3}
                                   name="motif"
                                   value={rdvForm.motif}
                                   onChange={handleRdvChange}
                                   placeholder="Motif de la consultation"
                                 />
                               </Form.Group>
                               
                               <Form.Group className="mb-3">
                                 <Form.Label>Statut</Form.Label>
                                 <Form.Select
                                   name="statut"
                                   value={rdvForm.statut}
                                   onChange={handleRdvChange}
                                 >
                                   <option value="confirmé">Confirmé</option>
                                   <option value="en attente">En attente</option>
                                   <option value="annulé">Annulé</option>
                                 </Form.Select>
                               </Form.Group>
                               
                               <div className="d-flex justify-content-end mt-3">
                                 <Button
                                   variant="secondary"
                                   onClick={() => setShowRdvModal(false)}
                                   className="me-2"
                                   disabled={loading}
                                 >
                                   <FontAwesomeIcon icon={faTimes} className="me-1" />
                                   Annuler
                                 </Button>
                                 <Button
                                   variant="success"
                                   type="submit"
                                   disabled={loading}
                                 >
                                   {loading ? (
                                     <>
                                       <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                       Ajout en cours...
                                     </>
                                   ) : (
                                     <>
                                       <FontAwesomeIcon icon={faCalendarPlus} className="me-1" />
                                       Ajouter le rendez-vous
                                     </>
                                   )}
                                 </Button>
                               </div>
                             </Form>
                           </Modal.Body>
                         </Modal>
                         
                         {/* Modal pour générer un QR code */}
                         <Modal 
                           show={showQRModal} 
                           onHide={() => setShowQRModal(false)}
                           backdrop="static"
                         >
                           <Modal.Header closeButton>
                             <Modal.Title>
                               <FontAwesomeIcon icon={faQrcode} className="me-2" />
                               Générer un QR code pour vos patients
                             </Modal.Title>
                           </Modal.Header>
                           <Modal.Body>
                             {!qrGenerated ? (
                               <Form>
                                 <div className="alert alert-info">
                                   <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                   Ce QR code permettra à vos patients de prendre rendez-vous ou de vérifier l'état d'un rendez-vous existant.
                                 </div>
                                 
                                 <Form.Group className="mb-3">
                                   <Form.Label>Structure ou Prestataire de santé  <span className="text-danger">(Obligatoire)</span></Form.Label>
                                   <Form.Select
                                     value={qrStructureId}
                                     onChange={(e) => setQrStructureId(e.target.value)}
                                     required
                                   >
                                     <option value="">Sélectionner une structure</option>
                                     {affiliatedStructures.map(structure => (
                                       <option key={structure.id} value={structure.id}>
                                         {structure.nom}
                                       </option>
                                     ))}
                                   </Form.Select>
                                   <Form.Text className="text-muted">
                                     Sélectionnez la structure pour laquelle vous souhaitez générer un QR code.
                                   </Form.Text>
                                 </Form.Group>
                                 
                                 <div className="d-flex justify-content-end mt-3">
                                   <Button
                                     variant="secondary"
                                     onClick={() => setShowQRModal(false)}
                                     className="me-2"
                                     disabled={loading}
                                   >
                                     <FontAwesomeIcon icon={faTimes} className="me-1" />
                                     Annuler
                                   </Button>
                                   <Button
                                     variant="primary"
                                     onClick={handleGenerateQR}
                                     disabled={loading || !qrStructureId}
                                   >
                                     {loading ? (
                                       <>
                                         <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                         Génération en cours...
                                       </>
                                     ) : (
                                       <>
                                         <FontAwesomeIcon icon={faQrcode} className="me-1" />
                                         Générer le QR code
                                       </>
                                     )}
                                   </Button>
                                 </div>
                               </Form>
                             ) : (
                               <div className="text-center">
                                 <h5 className="mb-3">QR code généré avec succès</h5>
                                 <div className="qr-code-container p-3 bg-white d-inline-block mb-3">
                                   <QRCodeCanvas 
                                     id="qr-code"
                                     value={qrUrl} 
                                     size={200} 
                                     level="H"
                                     includeMargin={true}
                                   />
                                 </div>
                                 
                                 <div className="mb-3">
                                   <p className="mb-1"><strong>URL du QR code:</strong></p>
                                   <div className="input-group">
                                     <input 
                                       type="text" 
                                       className="form-control" 
                                       value={qrUrl} 
                                       readOnly 
                                     />
                                     <button 
                                       className="btn btn-outline-secondary" 
                                       type="button"
                                       onClick={copyToClipboard}
                                     >
                                       <FontAwesomeIcon icon={faLink} />
                                     </button>
                                   </div>
                                   {copySuccess && <small className="text-success">URL copiée!</small>}
                                 </div>
                                 
                                 <div className="d-flex justify-content-center mt-4">
                                   <Button
                                     variant="primary"
                                     onClick={downloadQRCode}
                                     className="me-2"
                                   >
                                     <FontAwesomeIcon icon={faDownload} className="me-1" />
                                     Télécharger le QR code
                                   </Button>
                                   <Button
                                     variant="secondary"
                                     onClick={() => setShowQRModal(false)}
                                   >
                                     <FontAwesomeIcon icon={faCheck} className="me-1" />
                                     Fermer
                                   </Button>
                                 </div>
                                 
                                 <div className="alert alert-info mt-4">
                                   <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                   <p className="mb-0">
                                     <strong>Comment utiliser ce QR code?</strong>
                                   </p>
                                   <ul className="mt-2 mb-0 text-start">
                                     <li>Imprimez ce QR code et affichez-le dans votre salle d'attente</li>
                                     <li>Les patients peuvent le scanner avec leur smartphone</li>
                                     <li>Ils pourront alors prendre un rendez-vous ou vérifier l'état d'un rendez-vous existant</li>
                                     <li>Si le patient n'est pas inscrit, il sera guidé pour créer un compte</li>
                                   </ul>
                                 </div>
                               </div>
                             )}
                           </Modal.Body>
                         </Modal>
                         
                         {/* Modal de confirmation de suppression */}
                         <Modal 
                           show={showDeleteModal} 
                           onHide={() => setShowDeleteModal(false)}
                           backdrop="static"
                           centered
                         >
                           <Modal.Header closeButton>
                             <Modal.Title className="text-danger">
                               <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                               Confirmer la suppression
                             </Modal.Title>
                           </Modal.Header>
                           <Modal.Body>
                             {patientToDelete && (
                               <>
                                 <p>Êtes-vous sûr de vouloir supprimer le patient <strong> {patientToDelete.nom}</strong> ?</p>
                                 <div className="alert alert-warning">
                                   <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                   <strong>Attention :</strong> Cette action est irréversible et supprimera également tous les rendez-vous associés à ce patient.
                                 </div>
                               </>
                             )}
                           </Modal.Body>
                           <Modal.Footer>
                             <Button 
                               variant="secondary" 
                               onClick={() => setShowDeleteModal(false)}
                               disabled={loading}
                             >
                               <FontAwesomeIcon icon={faTimes} className="me-1" />
                               Annuler
                             </Button>
                             <Button 
                               variant="danger" 
                               onClick={handleDeletePatient}
                               disabled={loading}
                             >
                               {loading ? (
                                 <>
                                   <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                   Suppression en cours...
                                 </>
                               ) : (
                                 <>
                                   <FontAwesomeIcon icon={faTrash} className="me-1" />
                                   Confirmer la suppression
                                 </>
                               )}
                             </Button>
                           </Modal.Footer>
                         </Modal>
                         
                         {/* Liste des patients avec barre de recherche */}
                         <div className="patients-list mt-4">
                           <div className="d-flex justify-content-between align-items-center mb-3">
                             <h5>
                               <FontAwesomeIcon icon={faUser} className="me-2" />
                               Patients ({patients.length})
                             </h5>
                             <div className="search-container">
                               <div className="input-group" style={{ maxWidth: '300px' }}>
                                 <input
                                   type="text"
                                   className="form-control"
                                   placeholder="Rechercher un patient..."
                                   value={searchTerm}
                                   onChange={(e) => setSearchTerm(e.target.value)}
                                 />
                                 {searchTerm && (
                                   <button 
                                     className="btn btn-outline-secondary" 
                                     type="button"
                                     onClick={() => setSearchTerm('')}
                                   >
                                     <FontAwesomeIcon icon={faTimes} />
                                   </button>
                                 )}
                               </div>
                             </div>
                           </div>
                           
                           {loading && !patients.length ? (
                             <div className="text-center py-4">
                               <Spinner animation="border" variant="primary" />
                               <p className="mt-2">Chargement des patients...</p>
                             </div>
                           ) : patients.length === 0 ? (
                             <div className="alert alert-info">
                               <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                               Aucun patient enregistré. Ajoutez votre premier patient en cliquant sur "Ajouter un patient".
                             </div>
                           ) : filteredPatients.length === 0 ? (
                             <div className="alert alert-info">
                               <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                               Aucun patient ne correspond à votre recherche.
                             </div>
                           ) : (
                             <div className="table-responsive">
                               <table className="table table-hover">
                                 <thead>
                                   <tr>
                                     <th>Nom</th>
                                     <th>Téléphone</th>
                                     <th>Email</th>
                                     <th>Date de naissance</th>
                                     <th className="text-center">Actions</th>
                                   </tr>
                                 </thead>
                                 <tbody>
                                   {filteredPatients.map(patient => (
                                     <tr key={patient.id}>
                                       <td>{patient.nom}</td>
                                       <td>{patient.telephone}</td>
                                       <td>{patient.email}</td>
                                       <td>{new Date(patient.dateNaissance).toLocaleDateString()}</td>
                                       <td className="text-center">
                                         <Button
                                           variant="outline-primary"
                                           size="sm"
                                           className="me-2"
                                           onClick={() => handleEditPatient(patient)}
                                           title="Modifier"
                                         >
                                           <FontAwesomeIcon icon={faEdit} />
                                         </Button>
                                         <Button
                                           variant="outline-danger"
                                           size="sm"
                                           onClick={() => {
                                             setPatientToDelete(patient);
                                             setShowDeleteModal(true);
                                           }}
                                           title="Supprimer"
                                         >
                                           <FontAwesomeIcon icon={faTrash} />
                                         </Button>
                                       </td>
                                     </tr>
                                   ))}
                                 </tbody>
                               </table>
                             </div>
                           )}
                         </div>
                       </div>
                     );
                   };
                   
                   export default MedecinPatientManager;
                   